import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const lbl = { display: 'block', marginBottom: '0.3rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

const fmtHora = (v) => v ? new Date(v).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';

const panelCardStyle = {
    background: '#fff', borderRadius: 14, padding: '1.25rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)', height: '100%',
};

// ════════════════════════════════════════════════════════════════════════════
// Pantalla de monitor para el Portero: simulador de barrera + iris, y un
// tablero en vivo con 4 paneles: últimos ingresos / quién está ingresando /
// quién está saliendo / últimos egresos.
// ════════════════════════════════════════════════════════════════════════════

const ControlAcceso = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio, seleccionarBarrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(false);

    const [personas, setPersonas] = useState([]);
    const [fotosPersonas, setFotosPersonas] = useState({});
    const [personaSimulada, setPersonaSimulada] = useState(null);
    const [simulando, setSimulando] = useState(false);

    const [ultimoIngreso, setUltimoIngreso] = useState(null);
    const [ultimoEgreso, setUltimoEgreso] = useState(null);

    // ─── Auto-selección de barrio para portero ────────────────────────────────
    useEffect(() => {
        if (barrio?.id) return;
        const barriosPermitidos = JSON.parse(localStorage.getItem('barriosPermitidos') || '[]');
        if (barriosPermitidos.length === 0) return;
        axios.get(`${HOST_API}api/barrios/GetBarrios`, {
            params: { barrioId: barriosPermitidos[0], administracionId }
        }).then(({ data }) => {
            const lista = Array.isArray(data) ? data : [data];
            const b = lista[0];
            if (b) seleccionarBarrio(b.barrioId, b.nombreBarrio, b.cuit, b.direccion, b.razonSocial, b.numeroCuenta, b.cuentaBancaria, b.tipoCuentaBancaria, b.alias);
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Carga personas + fotos (para el simulador y para resolver fotos) ─────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/personas/GetPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`)
            .then(({ data }) => setPersonas(data || []))
            .catch(() => {});
        axios.get(`${HOST_API}api/personas/GetFotosPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`)
            .then(({ data }) => {
                const mapa = {};
                (data || []).forEach((f) => { mapa[f.personaId] = f.fotoUrl; });
                setFotosPersonas(mapa);
            })
            .catch(() => {});
    }, [HOST_API, barrio?.id, administracionId]);

    // ─── Fetch del historial de hoy (últimos ingresos/egresos) ────────────────
    // Se usa GetHistorial (no GetDentroDelBarrio) porque ese último deja de
    // incluir a una persona apenas se le registra el egreso: acá necesitamos
    // que el movimiento quede visible aunque ya haya salido.
    const fetchTodo = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const hoy = new Date().toISOString().split('T')[0];
            const { data } = await axios.get(`${HOST_API}api/controlAcceso/GetHistorial`, {
                params: { administracionId, barrioId: barrio.id, fechaDesde: hoy, fechaHasta: hoy },
            });
            setHistorial(data || []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el control de acceso', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, administracionId]);

    useEffect(() => { fetchTodo(); }, [fetchTodo]);

    // Refresco automático liviano, para que el tablero se mantenga al día sin
    // depender de que alguien clickee "Actualizar".
    useEffect(() => {
        const id = setInterval(fetchTodo, 20000);
        return () => clearInterval(id);
    }, [fetchTodo]);

    const ultimosIngresos = useMemo(() =>
        [...historial].filter(r => r.fechaIngreso)
            .sort((a, b) => new Date(b.fechaIngreso) - new Date(a.fechaIngreso))
            .slice(0, 8),
    [historial]);

    const ultimosEgresos = useMemo(() =>
        [...historial].filter(r => r.fechaEgreso)
            .sort((a, b) => new Date(b.fechaEgreso) - new Date(a.fechaEgreso))
            .slice(0, 8),
    [historial]);

    const fotoPorDni = useCallback((dni) => {
        if (!dni) return null;
        const persona = personas.find(p => p.dni === dni);
        return persona ? fotosPersonas[persona.personaId] : null;
    }, [personas, fotosPersonas]);

    const urlFoto = (fotoUrl) => `${HOST_API}${fotoUrl}`.replace(/([^:])\/\/+/g, '$1/');

    // ─── Simulador de barrera + iris (demo, ver plan de hardware) ─────────────
    const simularEventoBarrera = async (evento) => {
        const persona = personas.find(p => p.personaId === personaSimulada);
        if (!persona) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccioná una persona para simular', life: 3000 }); return; }
        if (!persona.dni) { toast.current.show({ severity: 'warn', summary: 'Sin DNI', detail: 'Esa persona no tiene DNI cargado', life: 3000 }); return; }

        setSimulando(true);
        try {
            const { data } = await axios.post(`${HOST_API}api/controlAcceso/RegistrarEventoBarrera`, {
                administracionId, barrioId: barrio.id, dni: persona.dni, evento, fechaEvento: new Date(),
            });

            const detalle = {
                nombreVisitante: data?.nombreVisitante || persona.nombreCompleto,
                codigoUF: data?.codigoUF,
                patente: data?.patente,
                fotoUrl: fotosPersonas[persona.personaId],
                hora: new Date(),
            };
            if (evento === 'Ingreso') setUltimoIngreso(detalle);
            else setUltimoEgreso(detalle);

            fetchTodo();
        } catch (err) {
            toast.current.show({
                severity: 'error', summary: 'Error',
                detail: err?.response?.data?.errorDisplay || 'No se pudo simular el evento',
                life: 4000,
            });
        } finally {
            setSimulando(false);
        }
    };

    // ─── Templates ──────────────────────────────────────────────────────────────
    const PanelLista = ({ titulo, filas, campoFecha, icono, color }) => (
        <div style={panelCardStyle}>
            <div style={{ fontWeight: 700, color: '#101828', fontSize: '0.95rem', marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={icono} style={{ color }} /> {titulo}
            </div>
            {filas.length === 0 ? (
                <p style={{ color: '#98a2b3', fontSize: '0.85rem', textAlign: 'center', margin: '1.5rem 0' }}>Sin movimientos</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: 520, overflowY: 'auto' }}>
                    {filas.map((r) => {
                        const foto = fotoPorDni(r.dniConductor);
                        return (
                            <div key={r.controlAccesoId} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem', borderRadius: 8, background: '#f9fafb' }}>
                                {foto ? (
                                    <img src={urlFoto(foto)} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                                ) : (
                                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#eaecf0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#98a2b3', flexShrink: 0 }}>
                                        <i className="pi pi-user" />
                                    </div>
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {r.nombreVisitante}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#667085' }}>
                                        {t.UF} {r.codigoUF || '—'}{r.patente ? ` · ${r.patente}` : ''}
                                    </div>
                                </div>
                                <div style={{ fontSize: '0.72rem', color: '#98a2b3', flexShrink: 0 }}>{fmtHora(r[campoFecha])}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const PanelActual = ({ titulo, detalle, color, icono }) => (
        <div style={panelCardStyle}>
            <div style={{ fontWeight: 700, color, fontSize: '0.95rem', marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className={icono} /> {titulo}
            </div>
            {!detalle ? (
                <div style={{ padding: '2.5rem 0', textAlign: 'center', color: '#98a2b3' }}>
                    <i className="pi pi-user" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '0.5rem' }} />
                    Sin movimientos
                </div>
            ) : (
                <div style={{ textAlign: 'center' }}>
                    {detalle.fotoUrl ? (
                        <img src={urlFoto(detalle.fotoUrl)} alt=""
                            style={{ width: '100%', maxWidth: 340, aspectRatio: '1 / 1', borderRadius: '50%', objectFit: 'cover', border: `6px solid ${color}`, margin: '0 auto 1rem' }} />
                    ) : (
                        <div style={{
                            width: '100%', maxWidth: 340, aspectRatio: '1 / 1', borderRadius: '50%', background: '#f2f4f7', color: '#98a2b3',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
                            border: `6px solid ${color}`, fontSize: '6rem',
                        }}>
                            <i className="pi pi-user" />
                        </div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: '1.6rem', color: '#101828', marginBottom: '0.75rem' }}>{detalle.nombreVisitante}</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '0.5rem' }}>
                        <div>
                            <div style={{ fontSize: '0.75rem', color: '#667085', textTransform: 'uppercase' }}>{t.UF}</div>
                            <div style={{ fontWeight: 600, fontSize: '1.3rem' }}>{detalle.codigoUF || '—'}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '0.75rem', color: '#667085', textTransform: 'uppercase' }}>Patente</div>
                            <div style={{ fontWeight: 600, fontSize: '1.3rem' }}>{detalle.patente || '—'}</div>
                        </div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#98a2b3' }}>{fmtHora(detalle.hora)}</div>
                </div>
            )}
        </div>
    );

    // ─── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Control de Acceso</h4>
            <br />
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            <div className="flex align-items-center gap-2 mb-4">
                <Button label="Actualizar" icon="pi pi-refresh" className="p-button-sm" severity="secondary" onClick={fetchTodo} />
            </div>

            {/* ─── Simulador de barrera + iris (demo) ─────────────────────────
                No hay hardware instalado todavía: este panel llama al mismo
                endpoint que va a usar la barrera real (RegistrarEventoBarrera),
                para poder mostrar el flujo antes de tener el sensor físico. */}
            <div style={{
                border: '1px dashed #7f56d9', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: '1.5rem',
                background: '#f9f5ff', display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap',
            }}>
                <div style={{ marginRight: 'auto' }}>
                    <div style={{ fontWeight: 700, color: '#6941c6', fontSize: '0.85rem' }}>👁 Simulador Barrera + Iris (demo)</div>
                    <div style={{ fontSize: '0.75rem', color: '#7f56d9' }}>Simula el evento que va a mandar el hardware cuando esté instalado</div>
                </div>
                <div>
                    <label style={lbl}>Persona</label>
                    <Dropdown value={personaSimulada} options={personas} optionLabel="nombreCompleto" optionValue="personaId"
                        onChange={(e) => setPersonaSimulada(e.value)}
                        placeholder="Seleccionar" filter showClear className="p-inputtext-sm" style={{ width: 240 }}
                        itemTemplate={(p) => `${p.nombreCompleto} — DNI ${p.dni || '—'}`} />
                </div>
                <Button label="Simular ingreso" icon="pi pi-sign-in" className="p-button-sm" severity="help"
                    onClick={() => simularEventoBarrera('Ingreso')} loading={simulando} />
                <Button label="Simular egreso" icon="pi pi-sign-out" className="p-button-sm" severity="help" outlined
                    onClick={() => simularEventoBarrera('Egreso')} loading={simulando} />
            </div>

            {/* ─── Tablero en vivo: últimos ingresos / quién ingresa / quién sale / últimos egresos ─── */}
            <div className="grid">
                <div className="col-12 lg:col-2">
                    <PanelLista titulo="Últimos Ingresos" filas={ultimosIngresos} campoFecha="fechaIngreso" icono="pi pi-sign-in" color="#12b76a" />
                </div>
                <div className="col-12 lg:col-4">
                    <PanelActual titulo="Quién está ingresando" detalle={ultimoIngreso} color="#12b76a" icono="pi pi-sign-in" />
                </div>
                <div className="col-12 lg:col-4">
                    <PanelActual titulo="Quién está saliendo" detalle={ultimoEgreso} color="#1570ef" icono="pi pi-sign-out" />
                </div>
                <div className="col-12 lg:col-2">
                    <PanelLista titulo="Últimos Egresos" filas={ultimosEgresos} campoFecha="fechaEgreso" icono="pi pi-sign-out" color="#1570ef" />
                </div>
            </div>
        </>
    );
};

export default ControlAcceso;
